import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Euro, Edit, Trash2, Calendar, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ObraGastoForm, ObraGasto, TIPOS_GASTO } from './ObraGastoForm';
import { useToast } from '@/hooks/use-toast';

interface Props { obraId: string }

const fmt = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

export function ObraGastosTab({ obraId }: Props) {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const isOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';
  const [gastos, setGastos] = useState<ObraGasto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ObraGasto | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<string>('all');

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase.from('obra_gastos' as any) as any)
      .select('*').eq('obra_id', obraId).order('fecha', { ascending: false });
    if (!error) setGastos((data || []) as ObraGasto[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [obraId]);

  const filtered = useMemo(
    () => filtroTipo === 'all' ? gastos : gastos.filter(g => g.tipo === filtroTipo),
    [gastos, filtroTipo]
  );

  const total = useMemo(() => gastos.reduce((s, g) => s + Number(g.importe || 0), 0), [gastos]);

  const handleDelete = async (id: string) => {
    const { error } = await (supabase.from('obra_gastos' as any) as any).delete().eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Gasto eliminado' });
      load();
    }
  };

  const tipoLabel = (t: string) => TIPOS_GASTO.find(x => x.value === t)?.label || t;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2"><Euro className="h-5 w-5" /> Gastos de la obra</CardTitle>
            <CardDescription>
              Total: <span className="font-semibold text-foreground">{fmt(total)}</span> · {gastos.length} gastos
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {TIPOS_GASTO.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isOffice && (
              <Button onClick={() => { setEditing(null); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Añadir gasto
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm py-6 text-center">Cargando…</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6 text-center">
              {gastos.length === 0 ? 'No hay gastos registrados.' : 'No hay gastos de este tipo.'}
            </p>
          ) : (
            <div className="space-y-2">
              {filtered.map((g) => (
                <div key={g.id} className="flex flex-wrap items-start justify-between gap-2 p-3 border rounded-md">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary"><Tag className="h-3 w-3 mr-1" />{tipoLabel(g.tipo)}</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(g.fecha), 'dd/MM/yyyy', { locale: es })}
                      </span>
                      {g.proveedor && <span className="text-xs text-muted-foreground">· {g.proveedor}</span>}
                    </div>
                    <div className="font-medium">{g.descripcion}</div>
                    {g.notas && <div className="text-xs text-muted-foreground whitespace-pre-wrap">{g.notas}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="font-semibold">{fmt(Number(g.importe))}</div>
                      {g.iva_porcentaje != null && (
                        <div className="text-xs text-muted-foreground">IVA {g.iva_porcentaje}%</div>
                      )}
                    </div>
                    {isOffice && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(g); setShowForm(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(g.id!)} className="bg-destructive text-destructive-foreground">
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
          </DialogHeader>
          <ObraGastoForm
            obraId={obraId}
            gasto={editing}
            onSuccess={() => { setShowForm(false); setEditing(null); load(); }}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
